import type { ComponentId, XyzFieldSelection } from '../types';
import type { RawComponentValues } from './toXyzDataPoints';

/**
 * The whole of what these charts need to know about a component.
 *
 * Narrow on purpose. The bubble chart carries a ~30-method view interface and reads two
 * near-identical extraction paths off it, one per mode; everything here is satisfied by three
 * methods, so workspace mode and viewpoint mode become two small adapters over one extractor
 * rather than two copies of it. It also makes the extractor testable with an object literal
 * instead of a mocked interface.
 */
export type XyzComponentReader = {
  getFieldValue: (componentId: ComponentId, fieldName: string) => unknown;
  getLabel: (componentId: ComponentId) => string;
  getColors: (componentId: ComponentId) => { fill?: string };
};

/**
 * Field selections narrowed to what is actually plottable.
 *
 * All three dimensions are required, so this type cannot represent a chart with nothing to
 * draw. Every chart here reads z as its measure - a contour is the isolines of it, a surface
 * its relief, and a mark on a scatter has nowhere to sit without one - so an optional z would
 * be optional for none of them, and the components would be excluded one by one further along
 * for a reason the caller had already been handed.
 */
export type ResolvedXyzFields = {
  x: string;
  y: string;
  z: string;
  radius: string | null;
  color: string | null;
};

const inScope = (
  fieldName: string | null,
  fieldNamesInScope: ReadonlySet<string>
): string | null =>
  fieldName !== null && fieldNamesInScope.has(fieldName) ? fieldName : null;

/**
 * Returns null when there is nothing to plot, so the empty state is driven by an absent value
 * rather than by an empty point array whose cause has been lost.
 *
 * `fieldNamesInScope` is required rather than optional because a selection outside it is the
 * one case that looks plottable and is not. These settings persist per user across viewpoints,
 * so a field picked over Applications survives into a viewpoint over Servers - where the
 * pickers, which only offer what is in scope, read as empty while every component is silently
 * excluded for having no value. Treating it as unpicked is what the two agree on.
 */
export const resolveXyzFields = (
  selection: XyzFieldSelection,
  fieldNamesInScope: ReadonlySet<string>
): ResolvedXyzFields | null => {
  const x = inScope(selection.selectedFieldNameX, fieldNamesInScope);
  const y = inScope(selection.selectedFieldNameY, fieldNamesInScope);
  const z = inScope(selection.selectedFieldNameZ, fieldNamesInScope);
  if (!x || !y || !z) {
    return null;
  }

  return {
    x,
    y,
    z,
    radius: inScope(selection.selectedFieldNameRadius, fieldNamesInScope),
    color: inScope(selection.selectedFieldNameColor, fieldNamesInScope),
  };
};

export type ToXyzValuesArgs = {
  reader: XyzComponentReader;
  componentIds: ComponentId[];
  fields: ResolvedXyzFields;
  highlightedComponentId: ComponentId | null;
};

/**
 * Reads each component's selected fields, leaving coercion to {@link toXyzDataPoints}.
 *
 * An unselected optional field yields `undefined` rather than a lookup against the empty
 * string: asking a component for the field named `''` returns whatever `undefined` indexing
 * gives, which is indistinguishable from a real field that happens to be unset. Keeping the
 * two apart is what lets the next step tell "no colour field chosen" from "this component has
 * no colour value".
 */
export const toXyzValues = ({
  reader,
  componentIds,
  fields,
  highlightedComponentId,
}: ToXyzValuesArgs): RawComponentValues[] => {
  const read = (componentId: ComponentId, fieldName: string | null): unknown =>
    fieldName === null
      ? undefined
      : reader.getFieldValue(componentId, fieldName);

  return componentIds.map(componentId => {
    const { fill } = reader.getColors(componentId);

    return {
      componentId,
      label: reader.getLabel(componentId),
      x: reader.getFieldValue(componentId, fields.x),
      y: reader.getFieldValue(componentId, fields.y),
      z: read(componentId, fields.z),
      radius: read(componentId, fields.radius),
      colorValue: read(componentId, fields.color),
      /* Empty strings become null: the colour interfaces return `''` for a component whose
         type carries no colour, and a renderer has to fall back to the ramp there rather than
         asking a canvas to fill with the empty string, which it silently ignores. */
      fill: fill || null,
      isContext: highlightedComponentId === componentId,
    };
  });
};
